import { Flex, Text, palette } from "@minervaproject/fds";
import { format, formatISO } from "date-fns";
import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
} from "recharts";

import { useAppDispatch, useArraySelector } from "hooks/redux_hooks";
import { MonitoringEventHistogramDataType } from "shared/types/monitoring_event";
import { setTimeFilter } from "store/reducers/local_monitoring";
import { selectMonitoringEventHistogramData } from "store/selectors/monitoring";

const tickStyles = {
  fontFamily: "'acumin-pro', sans-serif",
  fontSize: "12px",
  fill: palette.blackTint40,
};
const CHART_MIN_HEIGHT = 100;

function MonitoringEventHistogramTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0].payload;
  return (
    <Flex
      direction="column"
      bgColor="blackTint20"
      p={2}
      style={{ borderRadius: "3px", maxWidth: "200px" }}
    >
      <Text kind="body-xs" color="white">
        {item.nEvents}
      </Text>
    </Flex>
  );
}

function MonitoringEventHistogram({
  selectedLevels,
  selectedTypes,
  selectedUsers,
}: {
  selectedLevels: string[];
  selectedTypes: string[];
  selectedUsers: string[];
}) {
  const dispatch = useAppDispatch();
  const data = useArraySelector(
    selectMonitoringEventHistogramData(selectedLevels, selectedTypes, selectedUsers),
  );
  const [refAreaLeft, setRefAreaLeft] = useState<number | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<number | null>(null);

  const labelFormatter = (value: string, index: number) => {
    if (index % 5 !== 0 || index === 0) return "";
    return format(new Date(Number(value)), "MMM dd, HH:mm:ss");
  };

  return (
    <ResponsiveContainer width="100%" height="95%" minHeight={CHART_MIN_HEIGHT}>
      <BarChart
        data={data}
        onMouseDown={(event) => {
          const item = event.activePayload?.[0]?.payload as MonitoringEventHistogramDataType;
          setRefAreaRight(null);
          setRefAreaLeft(item?.binMin);
        }}
        onMouseMove={(event) => {
          if (refAreaLeft) {
            const item = event.activePayload?.[0]?.payload as MonitoringEventHistogramDataType;
            setRefAreaRight(item?.binMin);
          }
        }}
        onMouseUp={(event) => {
          if (!refAreaLeft || !refAreaRight) {
            return;
          }
          const item = event.activePayload?.[0]?.payload as MonitoringEventHistogramDataType;
          dispatch(
            setTimeFilter({
              startTimeFilter: formatISO(refAreaLeft),
              endTimeFilter: formatISO(item?.binMax),
            }),
          );
          setRefAreaLeft(null);
          setRefAreaRight(null);
        }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="binMin"
          interval={0}
          stroke={palette.blackTint90}
          tick={tickStyles}
          tickFormatter={labelFormatter}
          tickLine={false}
        />
        <Tooltip
          content={<MonitoringEventHistogramTooltip />}
          cursor={false}
          isAnimationActive={false}
        />
        <Bar dataKey="nEvents" isAnimationActive={false}>
          {data.map((item) => (
            <Cell key={`cell-${item.binMin}`} fill={palette.greenTint20} />
          ))}
        </Bar>
        {refAreaLeft && refAreaRight ? (
          <ReferenceArea x1={refAreaLeft} x2={refAreaRight} strokeOpacity={0.3} />
        ) : null}
      </BarChart>
    </ResponsiveContainer>
  );
}

export { MonitoringEventHistogramTooltip, MonitoringEventHistogram };
